import {
  FileFolderItem,
  isModelLoading,
  isModelVisible,
  useCapabilityRegistryContext,
  useDataContext,
} from '@mobile-mapping/providers';
import { FitToViewModelCapabilityCoordinator } from '@trimble/web3d';
import { SelectionSyncCoordinator } from '@trimble/pane-sync';
import { ModelTree } from '@trimble/project-data-models-tree';
import { getTrbModelName } from '@trimble/web3d-providers';
import { createMemo, JSX } from 'solid-js';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import { handleNodeSelectionSync } from '../tree-helpers';
import { useLogger } from '@trimble/solidjs-utils';
import { ProjectDataTreeContainerProps } from './shared-panel.types';

export interface SharedModelsTreeContainerProps
  extends ProjectDataTreeContainerProps<FileFolderItem> {
  onModelDetailsSelect?(model: FileFolderItem): void;
}

export const SharedModelsTreeContainer = (
  props: SharedModelsTreeContainerProps
): JSX.Element => {
  const { dataState, dataLoadingService } = useDataContext();
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const logger = useLogger().getLogger('SharedModelsTreeContainer');

  const fitToViewCoordinator = capabilityRegistry.get(
    FitToViewModelCapabilityCoordinator
  );
  const selectionSyncCoordinator = capabilityRegistry.get(
    SelectionSyncCoordinator
  );

  const isNodeSelected = (nodeId: string): boolean =>
    selectionSyncCoordinator()
      ?.selectedModelIds()
      .has(getTrbModelName(nodeId)) ?? false;

  const handleNodeSelect = (
    nodeId: string,
    node: TreeNodeDefinition<FileFolderItem>,
    e: MouseEvent
  ): void => {
    void handleNodeSelectionSync(
      getTrbModelName(nodeId),
      node,
      e,
      (id) => isModelVisible(dataState, id),
      selectionSyncCoordinator()
    ).catch((error) => logger.error('Error selecting node', node, error));
  };

  const tcFileFolder = createMemo(() => {
    if (props.nodes !== undefined) {
      const items = Object.fromEntries(
        props.nodes.flatMap((node) =>
          node.data === undefined ? [] : [[node.id, node.data]]
        )
      ) as Record<string, FileFolderItem>;

      return { ...dataState.tcFileFolder, items };
    }

    return dataState.tcFileFolder;
  });

  return (
    <ModelTree
      tcFileFolder={tcFileFolder()}
      isNodeLoading={(nodeId) => isModelLoading(dataState, nodeId)}
      isNodeVisible={(nodeId) => isModelVisible(dataState, nodeId)}
      onVisibilityToggle={(nodeId) => {
        if (
          isModelLoading(dataState, nodeId) ||
          isModelVisible(dataState, nodeId)
        ) {
          dataLoadingService.unload('model', nodeId);
        } else {
          dataLoadingService.load('model', nodeId);
        }
      }}
      onFitToView={(item) => {
        void fitToViewCoordinator()?.fitToView(getTrbModelName(item.id));
      }}
      onSeeDetails={props.onModelDetailsSelect}
      isNodeSelected={isNodeSelected}
      onNodeSelect={handleNodeSelect}
    />
  );
};
